import { Container, Graphics, Sprite } from 'pixi.js';
import gsap from 'gsap';
import { COLORS, FONT, SCREEN, SHAPE, TIMING } from '../config/tokens';
import type { Layout, Screen } from '../render/stage';
import { Fx } from '../render/fx';
import { background, frame } from '../assets/loader';
import { dogFrame, type DogId } from '../assets/names';
import { after, button, currencyChip, iconButton, label, pressable, titleChip, toast, type CurrencyChip } from '../ui/components';
import { CAFE_ITEMS, type CafeItem } from '../meta/cafe';
import { DOGS, dogInfo } from '../meta/dogs';
import type { Nav } from '../meta/nav';
import { buyNextCafeItem, isUnlocked, nextCafeItem, type ProgressEvent } from '../meta/progress';
import { sound } from '../audio/sound';
import { markStarterShown, shouldOfferStarter, today } from '../monetization/products';
import { openStarterOffer } from '../ui/offers';
import { haptic } from '../audio/haptics';

/** Floor colour of cafe_room_empty – extends the room on tall phones. */
const FLOOR = 0xcda17e;
const ROOM_W = 1024;
const ROOM_H = 1536;
/** where dogs stand in the room (room px), front-most last */
const DOG_SPOTS = [
  { x: 520, y: 1140 }, { x: 300, y: 1250 }, { x: 740, y: 1260 }, { x: 520, y: 1390 },
  { x: 220, y: 1400 }, { x: 820, y: 1410 }, { x: 650, y: 1030 },
];
/** spots are spread between these room heights; squeezed upwards when the buttons need room */
const FLOOR_TOP = 1000;
const FLOOR_BOTTOM = 1420;
const DOG_HEIGHT = 250;

/**
 * The player's home (mockups 26 before / 27 after). Furniture is bought
 * with stars one piece at a time; every purchase also gives a waiting,
 * dirty rescue dog a home ("… found a home!").
 */
export class CafeScreen implements Screen {
  readonly view = new Container();
  private readonly room = new Container();
  private readonly floorFill = new Graphics();
  private readonly damage = new Container();
  private readonly furniture = new Container();
  private readonly dogsLayer = new Container();
  private readonly hud = new Container();
  private readonly bottom = new Container();
  private readonly fx = new Fx();
  private readonly overlay = new Container();
  private stars!: CurrencyChip;
  private coins!: CurrencyChip;
  private dogSprites = new Map<DogId, Sprite>();
  /** unsqueezed room y of each dog (layout maps it onto the visible floor) */
  private dogBaseY = new Map<Sprite, number>();
  private l?: Layout;
  private busy = false;

  constructor(private readonly nav: Nav, private readonly notes: ProgressEvent[] = []) {
    const bg = new Sprite(background('cafe_room_empty'));
    this.room.addChild(bg, this.damage, this.furniture, this.dogsLayer);
    this.view.addChild(this.floorFill, this.room, this.fx.view, this.hud, this.bottom, this.overlay);
    this.drawDamage();
    this.damage.visible = !nav.save().cafe.includes('walls');
    for (const id of nav.save().cafe) this.placeItem(CAFE_ITEMS.find((i) => i.id === id)!, false);
    this.placeDogs();
    this.buildHud();
    this.buildBottom();
  }

  destroy(): void {
    this.fx.destroy();
  }

  // ------------------------------------------------------------ room

  /** "Before" state: cracks, wall patches and dirt spots, drawn in code (no asset yet). */
  private drawDamage(): void {
    const g = new Graphics();
    const crack = (pts: number[]) => {
      g.moveTo(pts[0], pts[1]);
      for (let i = 2; i < pts.length; i += 2) g.lineTo(pts[i], pts[i + 1]);
      g.stroke({ width: 5, color: COLORS.cocoa, cap: 'round', join: 'round' });
    };
    crack([400, 170, 385, 215, 405, 245, 380, 300]);
    crack([405, 245, 430, 262]);
    crack([690, 330, 712, 380, 695, 420, 718, 470]);
    crack([880, 520, 905, 560, 890, 600]);
    for (const [x, y, rx, ry] of [[930, 320, 70, 55], [860, 440, 45, 36], [95, 560, 55, 42], [990, 700, 40, 60], [600, 180, 35, 28]]) {
      g.ellipse(x, y, rx, ry).fill(COLORS.latte).stroke({ width: 4, color: COLORS.wood, alpha: 0.5 });
    }
    for (const [x, y, rx] of [[330, 1000, 40], [520, 1180, 55], [760, 1060, 34], [880, 1300, 48], [220, 1300, 30], [640, 1350, 42]]) {
      g.ellipse(x, y, rx, rx * 0.45).fill({ color: COLORS.wood, alpha: 0.28 });
    }
    this.damage.addChild(g);
  }

  private placeItem(item: CafeItem, animate: boolean): Sprite | null {
    if (!item.frame) {
      // wall repair: the damage fades away (§13 renovation 600 ms)
      if (animate) gsap.to(this.damage, { alpha: 0, duration: TIMING.renovate / 1000, onComplete: () => (this.damage.visible = false) });
      else this.damage.visible = false;
      return null;
    }
    const s = new Sprite(frame(item.frame));
    s.anchor.set(...item.anchor);
    const sc = item.width / s.texture.width;
    s.scale.set(sc);
    s.position.set(item.x, item.y);
    s.zIndex = item.layer;
    this.furniture.sortableChildren = true;
    this.furniture.addChild(s);
    if (animate) {
      // §13 furniture: 220 ms, 95 → 100 %
      s.alpha = 0;
      s.scale.set(sc * 0.95);
      gsap.to(s, { alpha: 1, duration: TIMING.furniturePlace / 1000 });
      gsap.to(s.scale, { x: sc, y: sc, duration: 0.45, ease: 'elastic.out(1, 0.5)' });
    }
    return s;
  }

  private placeDogs(): void {
    this.dogsLayer.removeChildren().forEach((c) => c.destroy());
    this.dogSprites.clear();
    this.dogBaseY.clear();
    const save = this.nav.save();
    const present = DOGS.filter((d) => save.dogs[d.id]);
    const hasBed = save.cafe.includes('dog_bed');
    // the first settled dog naps in the bed; everyone else gets a spot on the floor
    const sleeper = hasBed ? present.find((d) => save.dogs[d.id] === 'happy' && present.length > 1) : undefined;
    let spot = 0;
    for (const d of present) {
      const state = save.dogs[d.id]!;
      const sleeping = d === sleeper;
      const s = new Sprite(frame(dogFrame(d.id, sleeping ? 'sleeping' : state === 'rescued' ? 'rescued' : 'happy')));
      s.anchor.set(0.5, 1);
      const h = sleeping ? DOG_HEIGHT * 0.55 : DOG_HEIGHT;
      const sc = h / s.texture.height;
      s.scale.set(sc);
      const pos = sleeping ? { x: 250, y: 975 } : DOG_SPOTS[spot++ % DOG_SPOTS.length];
      s.position.set(pos.x, pos.y);
      s.zIndex = pos.y;
      if (!sleeping) this.dogBaseY.set(s, pos.y);
      // §13 breathing: 2.5–3.5 s, tiny scale
      gsap.to(s.scale, { y: sc * (sleeping ? 1.04 : 1.02), x: sc * 0.99, duration: 1.3 + Math.random() * 0.4, yoyo: true, repeat: -1, ease: 'sine.inOut' });
      s.eventMode = 'static';
      s.cursor = 'pointer';
      s.on('pointertap', () => this.petDog(d.id, s, sc));
      this.dogsLayer.addChild(s);
      this.dogSprites.set(d.id, s);
    }
    this.dogsLayer.sortableChildren = true;
  }

  /** Tapping a dog: happy hop, hearts and one of its personality lines. */
  private petDog(id: DogId, s: Sprite, sc: number): void {
    sound.unlock();
    sound.play('praise', { pitch: 1.3 });
    haptic('light');
    gsap.killTweensOf(s);
    const y = s.y;
    gsap.timeline()
      .to(s.scale, { x: sc * 1.08, y: sc * 0.9, duration: 0.08 })
      .to(s, { y: y - 45, duration: 0.18, ease: 'power2.out' })
      .to(s.scale, { x: sc * 0.96, y: sc * 1.06, duration: 0.18 }, '<')
      .to(s, { y, duration: 0.18, ease: 'power2.in' })
      .to(s.scale, { x: sc, y: sc, duration: 0.3, ease: 'elastic.out(1, 0.5)' });
    const head = this.view.toLocal(s.toGlobal({ x: 0, y: -s.texture.height }));
    this.fx.burst(head.x, head.y, { frame: 'vfx_heart', count: 3, speed: 140, gravity: -160, size: 40, life: 0.8, angle: -Math.PI / 2, spread: 1.2 });
    const info = dogInfo(id);
    const state = this.nav.save().dogs[id];
    const line = state === 'rescued' ? 'Waiting for a cozy home…' : info.lines[Math.floor(Math.random() * info.lines.length)];
    this.bubble(`${info.name}: ${line}`, head.x, head.y - 30);
  }

  private bubble(text: string, x: number, y: number): void {
    const t = label(text, 28, { weight: FONT.weight.body });
    t.style.wordWrap = true;
    t.style.wordWrapWidth = 460;
    t.style.align = 'center';
    const w = Math.min(520, t.width + 48);
    const h = t.height + 36;
    const c = new Container();
    c.addChild(new Graphics().roundRect(-w / 2, -h, w, h, 24).fill(COLORS.cream).stroke({ width: SHAPE.outline, color: COLORS.cocoa })
      .poly([-14, -2, 14, -2, 0, 18]).fill(COLORS.cream), t);
    t.position.set(0, -h / 2);
    c.position.set(Math.min(Math.max(x, w / 2 + 20), SCREEN.width - w / 2 - 20), y);
    this.overlay.addChild(c);
    c.scale.set(0.6);
    gsap.timeline({ onComplete: () => c.destroy({ children: true }) })
      .to(c.scale, { x: 1, y: 1, duration: 0.25, ease: 'back.out(2)' })
      .to(c, { alpha: 0, duration: 0.3 }, '+=2.2');
  }

  // ------------------------------------------------------------ HUD

  private buildHud(): void {
    const save = this.nav.save();
    const title = titleChip('Your Café');
    title.position.set(SCREEN.width / 2, 0);
    this.stars = currencyChip('vfx_star', save.stars);
    this.stars.view.position.set(150, 110);
    this.coins = currencyChip('ui_coin', save.coins);
    this.coins.view.position.set(SCREEN.width - 150, 110);
    if (isUnlocked(save, 'shop') || this.nav.debug) pressable(this.coins.view, () => this.nav.showShop());
    this.hud.addChild(title, this.stars.view, this.coins.view);
  }

  private buildBottom(): void {
    this.bottom.removeChildren().forEach((c) => c.destroy({ children: true }));
    const save = this.nav.save();
    const item = nextCafeItem(save);
    let y = 0;
    if (item) {
      const affordable = save.stars >= item.cost;
      const text = affordable
        ? item.id === 'walls' ? 'Fix up the café' : `${item.name} · ${item.cost}★`
        : `${item.name} · ${save.stars}/${item.cost}★`;
      const fix = button(text, 640, affordable ? 'primary' : 'disabled', () => this.renovate(), 100);
      fix.position.set(SCREEN.width / 2, y);
      this.bottom.addChild(fix);
      if (!affordable) {
        const hint = label('Win levels to earn stars', 26, { weight: FONT.weight.body });
        hint.position.set(SCREEN.width / 2, y + 72);
        this.bottom.addChild(hint);
      }
      y += 150;
    }
    const play = button(`Play Level ${save.nextLevel}`, 640, item && save.stars >= item.cost ? 'secondary' : 'primary', () => this.nav.playLevel(save.nextLevel), 100);
    play.position.set(SCREEN.width / 2, y);
    this.bottom.addChild(play);
    y += 150;

    const nav: [Parameters<typeof iconButton>[0], string, () => void, boolean][] = [
      ['dogs', 'Dogs', () => this.nav.showDogs(), true],
      ['map', 'Map', () => this.nav.showMap(), isUnlocked(save, 'map') || this.nav.debug],
      ['shop', 'Shop', () => this.nav.showShop(), isUnlocked(save, 'shop') || this.nav.debug],
      ['settings', 'Settings', () => this.nav.showSettings(this.overlay), true],
    ];
    const shown = nav.filter((n) => n[3]);
    shown.forEach(([kind, caption, onTap], i) => {
      const b = iconButton(kind, onTap, caption);
      b.position.set(SCREEN.width / 2 + (i - (shown.length - 1) / 2) * 190, y);
      this.bottom.addChild(b);
    });
    this.positionBottom();
  }

  private positionBottom(): void {
    if (!this.l) return;
    // bottom block: [fix-up] [play] [nav icons + captions]
    const block = (nextCafeItem(this.nav.save()) ? 150 : 0) + 150 + 70;
    this.bottom.y = this.l.safeBottom - block;
  }

  // ------------------------------------------------------------ renovation

  private renovate(): void {
    if (this.busy) return;
    const result = buyNextCafeItem(this.nav.save());
    if (!result) return;
    this.busy = true;
    this.nav.update(result.save);
    const { item, cleaned } = result;
    this.stars.set(result.save.stars);
    sound.play('create');
    haptic('success');
    const placed = this.placeItem(item, true);
    const p = placed ? this.view.toLocal(placed.getGlobalPosition()) : { x: SCREEN.width / 2, y: 500 };
    const cy = placed && item.anchor[1] === 1 ? p.y - 80 : p.y + 60;
    this.fx.burst(p.x, cy, { frame: 'vfx_steam', count: 5, speed: 160, gravity: -120, size: 90, life: 0.7 });
    this.fx.burst(p.x, cy, { frame: 'vfx_sparkle', count: 10, speed: 320, gravity: 0, size: 34 });
    if (!placed) this.fx.confetti(this.l?.left ?? 0, (this.l?.right ?? SCREEN.width) - (this.l?.left ?? 0), 0, 40);
    this.buildBottom();
    after(this.view, cleaned ? 900 : 400, () => {
      this.busy = false;
      if (cleaned) this.nav.showArrival(cleaned);
      else if (!nextCafeItem(this.nav.save())) {
        toast(this.overlay, 'Your café is complete!', (this.l?.safeTop ?? 92) + 220, 'vfx_heart');
        this.fx.confetti(this.l?.left ?? 0, (this.l?.right ?? SCREEN.width) - (this.l?.left ?? 0), 0, 90);
        sound.play('win');
      }
    });
  }

  /** Mockup 33: the optional starter pack, gently (see shouldOfferStarter). */
  private maybeOfferStarter(): void {
    const save = this.nav.save();
    if (!shouldOfferStarter(save, today(), isUnlocked(save, 'shop'))) return;
    // after the arrival / unlock notes have been read
    const delay = 900 + this.notes.length * 2800;
    after(this.view, delay, () => {
      if (this.busy) return;
      this.nav.update(markStarterShown(this.nav.save(), today()));
      openStarterOffer(this.overlay, this.l, this.nav, () => {
        this.stars.set(this.nav.save().stars, false);
        this.coins.set(this.nav.save().coins);
      });
    });
  }

  /** Announces what happened since the last visit (new dog, unlocks, gifts). */
  private announce(): void {
    const y = (this.l?.safeTop ?? 92) + 220;
    const later = (fn: () => void, i: number) => after(this.view, 500 + i * 2800, fn);
    this.notes.forEach((n, i) => {
      if (n.type === 'dog' && dogInfo(n.dog).location === 'cafe') {
        later(() => {
          toast(this.overlay, `${dogInfo(n.dog).name} needs a cozy home!`, y, dogFrame(n.dog, 'rescued'));
          const s = this.dogSprites.get(n.dog);
          if (s) this.fx.burst(s.x * this.room.scale.x + this.room.x, (s.y - DOG_HEIGHT) * this.room.scale.y + this.room.y, { frame: 'vfx_heart', count: 5, speed: 200, gravity: -150, size: 40 });
        }, i);
      } else if (n.type === 'unlock' && (n.what === 'shop' || n.what === 'map')) {
        later(() => toast(this.overlay, n.what === 'shop' ? 'New: the Shop!' : 'New: the World Map!', y, n.what === 'shop' ? 'ui_coin' : 'vfx_star'), i);
      } else if (n.type === 'booster') {
        const name = { hammer: 'Wooden Hammer', spoon: 'Stirring Spoon', moves5: '+5 Moves' }[n.booster];
        const icon = { hammer: 'booster_hammer', spoon: 'booster_spoon', moves5: 'booster_moves5' }[n.booster];
        later(() => toast(this.overlay, `Gift: ${n.amount}× ${name}`, y, icon), i);
      }
    });
  }

  layout(l: Layout): void {
    const first = !this.l;
    this.l = l;
    const viewW = l.right - l.left;
    // fit the room to the width, crop the sides a little on tall phones
    const s = Math.max(viewW / ROOM_W, (l.bottom / ROOM_H) * 0.8);
    this.room.scale.set(s);
    this.room.position.set(l.left + (viewW - ROOM_W * s) / 2, 0);
    this.floorFill.clear().rect(l.left, 0, viewW, l.bottom).fill(FLOOR);
    this.hud.y = l.safeTop + 10;
    this.positionBottom();
    // keep the dogs above the buttons: squeeze their floor band if needed
    const visibleBottom = (this.bottom.y - 70 - this.room.y) / s;
    const bottomY = Math.min(FLOOR_BOTTOM, visibleBottom);
    for (const [dog, y] of this.dogBaseY) {
      gsap.killTweensOf(dog);
      dog.y = FLOOR_TOP + ((y - FLOOR_TOP) * (bottomY - FLOOR_TOP)) / (FLOOR_BOTTOM - FLOOR_TOP);
    }
    if (first) {
      this.announce();
      this.maybeOfferStarter();
    }
  }
}
